<template>
    <Head :title="report?.name || t('teamPerspective.title')" />
    <BaseLayout gap="sm" class="fs-mask" fill-height>
        <header class="flex flex-wrap items-start gap-4 md:pr-10">
            <BaseLink :href="back_url" :aria-label="t('teamPage.allTeams')" class="mt-2 w-6 shrink-0 text-ink-muted hover:text-ink"
                ><i class="bi bi-chevron-left" aria-hidden="true"
            /></BaseLink>
            <h1 class="display-serif min-w-0 flex-1 break-words text-ink">{{ report?.name || t("teamPerspective.heading") }}</h1>
            <div v-if="report && (canAddMembers || leave_url || canDelete)" class="flex w-full items-center justify-end gap-2 md:w-auto">
                <ManageTeamDialog
                    v-if="canAddMembers"
                    :key="report.id"
                    :report="report"
                    :navigation="navigation"
                    :invitation-management="invitations"
                    @updated="membershipUpdated"
                    @completed="activeSection = 'members'" />
                <TeamActionsMenu
                    v-if="leave_url || canDelete"
                    :report="report"
                    :leave-url="leave_url"
                    :delete-url="delete_url"
                    @left="router.visit(back_url)"
                    @deleted="router.visit(back_url)" />
            </div>
        </header>
        <div v-if="!report" class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
            <p class="body-serif text-ink">{{ t("teamPerspective.noTeamsTitle") }}</p>
            <p class="body-small mt-1 text-ink-muted">{{ t("teamPerspective.noTeamsHint") }}</p>
        </div>
        <template v-else>
            <!-- Explicit aria-live keeps this region accessible while the management dialog is open. -->
            <p role="status" aria-live="polite" aria-atomic="true" class="sr-only">{{ reportStatus }}</p>
            <BaseTabs
                v-model="activeSection"
                :tab-ids="['overview', 'breakdown', 'members']"
                :labels="{ overview: t('teamPage.overview'), breakdown: t('teamPage.breakdown'), members: t('teamPage.members') }"
                :counts="{ members: report.members.length }"
                class="md:mx-10"
                content-class="pt-6"
                @update:model-value="
                    logUserInteraction(AmplitudeEventNames.TEAM_SECTION_SELECTED, { source: TEAM_INTERACTION_SOURCE.TEAM_PAGE, team_id: report.id, team_section: $event })
                ">
                <template #content-overview><TeamOverview :report="report" /></template>
                <template #content-members><TeamMembers :report="report" :navigation="navigation" :invitation-management="invitations" @updated="membershipUpdated" /></template>
                <template #content-breakdown>
                    <div class="flex flex-col gap-6">
                        <BaseCard v-if="!report.is_available" padding="lg"><TeamPerspectivePending :report="report" standalone /></BaseCard>
                        <div v-else-if="!dimensions.length" class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
                            <p class="body-serif text-ink">{{ t("teamPerspective.emptyTitle") }}</p>
                            <p class="body-small mt-1 text-ink-muted">{{ t("teamPerspective.empty") }}</p>
                        </div>
                        <TeamBreakdown v-else :key="report.id" :team-id="report.id" :dimensions="dimensions" :members="report.members" :viewer-id="report.viewer_id" />
                    </div>
                </template>
            </BaseTabs>
            <!-- One sharing note under every tab's cards, rather than a copy inside each tab. -->
            <p class="scaffold-details-small whitespace-pre-line text-ink-muted md:mx-10">{{ t("teamPerspective.sharing") }}</p>
            <footer class="sticky z-20 mt-auto pt-6 pb-4 md:bottom-0" :class="keyboardHeight ? 'bottom-0' : 'bottom-(--mobile-nav-total-height)'">
                <!-- Masks only the strip below the input's midline, so content stays visible right up to the input. -->
                <div class="absolute inset-x-0 top-1/2 bottom-0 bg-surface" aria-hidden="true" />
                <HomeChatInput
                    class="relative drop-shadow-md"
                    compact
                    :auto-focus-input="false"
                    :start-chat-url="home_chat_start_url"
                    chat-source="team_perspective"
                    :default-chat-redirect-url="default_chat_redirect_url"
                    :document-upload-enabled="document_upload_enabled"
                    :visual-document-analysis-enabled="visual_document_analysis_enabled"
                    :work-document-capabilities-url="work_document_capabilities_url"
                    :pii-patterns="pii_patterns?.map((pattern) => ({ ...pattern, keywords: pattern.keywords ?? null }))"
                    :better-voice-composer-enabled="better_voice_composer_enabled"
                    :placeholder="t('teamPage.askNadia')" />
            </footer>
        </template>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>
<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useUrlSearchParams } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { TeamPerspectivePage, TeamPerspectiveReport } from "~vue/api/generated/team-perspective/types.gen";
import { refreshSidebarNotificationsKey } from "~vue/appTypes";
import HomeChatInput from "~vue/ChatHome/HomeChatInput.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import { useKeyboard } from "~vue/composables/useKeyboard";
import ManageTeamDialog from "~vue/Perspective/ManageTeamDialog.vue";
import TeamActionsMenu from "~vue/Perspective/TeamActionsMenu.vue";
import TeamBreakdown from "~vue/Perspective/TeamBreakdown.vue";
import TeamMembers from "~vue/Perspective/TeamMembers.vue";
import TeamOverview from "~vue/Perspective/TeamOverview.vue";
import TeamPerspectivePending from "~vue/Perspective/TeamPerspectivePending.vue";
import { useTeamInvitationManagement } from "~vue/Perspective/useTeamInvitationManagement";
import BaseLink from "~vue/router/BaseLink.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
const props = defineProps<TeamPerspectivePage>();
const { t } = useI18n();
const { keyboardHeight } = useKeyboard();
const report = ref(props.report);
const isLead = computed(() => !!report.value?.add_members_url && report.value.members.some((member) => member.id === report.value?.viewer_id && member.role === "lead"));
const invitations = useTeamInvitationManagement(() => report.value?.id ?? "", isLead);
const refreshSidebarNotifications = inject(refreshSidebarNotificationsKey, async () => {});
let loadGeneration = 0;
onUnmounted(() => {
    loadGeneration += 1;
});
watch(
    () => props.report?.id,
    async (teamId) => {
        const generation = ++loadGeneration;
        if (!teamId || !props.report_url) {
            return;
        }
        const { data, error } = await useFetch(props.report_url).json<TeamPerspectiveReport>();
        if (generation !== loadGeneration) {
            return;
        }
        if (error.value || !data.value) {
            logError("Failed to load the opened team report");
            return;
        }
        const membershipId = data.value.viewer_membership_id;
        if (!membershipId) {
            return;
        }
        await nextTick();
        if (generation !== loadGeneration) {
            return;
        }
        const { error: acknowledgementError } = await useFetch(`/v1/teams/membership-notifications/${membershipId}/view`).post();
        if (acknowledgementError.value) {
            logError("Failed to acknowledge the opened team membership");
            return;
        }
        await refreshSidebarNotifications();
    },
    { immediate: true },
);
const reportStatus = computed(() => {
    const current = report.value;
    if (!current) {
        return "";
    }
    return current.is_available
        ? t("teamPage.reportAvailableStatus", { count: current.result_count })
        : t("teamPage.reportUnavailableStatus", { count: current.result_count, minimum: current.minimum_result_count });
});
const parameters = useUrlSearchParams("history", { writeMode: "push" });
const activeSection = computed({
    get: () => (["overview", "breakdown", "members"].includes(String(parameters.tab)) ? String(parameters.tab) : "overview"),
    set: (tab: string) => {
        parameters.tab = tab === "overview" ? "" : tab;
    },
});
let viewedTeamId: string | undefined;
watch(
    () => (report.value?.is_available && activeSection.value !== "members" ? report.value.id : undefined),
    (teamId) => {
        if (teamId && teamId !== viewedTeamId) {
            viewedTeamId = teamId;
            logUserInteraction(AmplitudeEventNames.TEAM_REPORT_VIEWED, { source: TEAM_INTERACTION_SOURCE.TEAM_PAGE, team_id: teamId, report_view: "full" });
        }
    },
    { immediate: true, flush: "post" },
);
const canAddMembers = computed(() => !!(report.value?.add_members_url && report.value.member_search_url));
const canDelete = computed(() => !!(props.delete_url && report.value?.can_manage));
const dimensions = computed(() => [...(report.value?.dimensions ?? [])].sort((a, b) => ["EI", "SN", "TF", "JP"].indexOf(a.id) - ["EI", "SN", "TF", "JP"].indexOf(b.id)));
watch(
    () => props.report,
    (value) => {
        report.value = value;
    },
);
function membershipUpdated(value: TeamPerspectiveReport) {
    report.value = value;
}
</script>
